import { MATERIALS, type MaterialId } from '../model/materials'
import { add, dist, dot, type Frame, makeFrame, normalize, scale, sub, toLocal, toWorld, toWorldDir, type V3 } from './geometry'
import { outwardNormal, pointInContour, pointInRegion } from './polygon'
import { coreRadius, coreShape, materialOf, outlineOf, type RoomInput, shapeOf, wallShape } from './room'

/**
 * First-order specular reflections. Every reflecting surface is one of two kinds:
 *  - a flat patch (box faces, floors, ceilings, cylinder end plates), solved by the image-source method;
 *  - a curved wall (cylinder sides, tube bores, round-room walls, ring cores), solved by finding the points on the
 *    cross-section ellipse where the path source → wall → listener is stationary (equal angles of incidence).
 * Curved walls can be convex (outside of a column) or concave (inside a tube or round hall).
 */

export interface PlaneReflector {
  kind: 'plane'
  id: string
  label: string
  material: MaterialId
  /** A point on the plane, and the unit normal pointing to the side that reflects. */
  o: V3
  n: V3
  /** Unit axes in the plane, and a test for whether in-plane coordinates (s, t) lie on the patch. */
  u: V3
  v: V3
  region: (s: number, t: number) => boolean
}

export interface CylReflector {
  kind: 'cyl'
  id: string
  label: string
  material: MaterialId
  /** Shape frame: axis = local Y, cross-section ellipse radii rx × rz, spanning local y ∈ [y0, y1]. */
  frame: Frame
  rx: number
  rz: number
  y0: number
  y1: number
  /** True when sound travels inside the wall's curve (tube bore, round hall); false for the outside of a column. */
  inward: boolean
}

/** Which primitive a surface belongs to (undefined for the room), used to keep second-order searches cheap. */
export interface Owner {
  id: string
  position: V3
  radius: number
}

export type Reflector = (PlaneReflector | CylReflector) & { owner?: Owner }

export interface Contact {
  point: V3
  /** Unit normal at the contact, pointing towards the side the sound arrives from. */
  normal: V3
}

export interface Candidate {
  /** 1 for a single bounce, 2 for two bounces (source → A → B → listener). */
  order: 1 | 2
  /** The first surface hit. */
  reflector: Reflector
  /** Stable across updates while the same surface patch keeps reflecting. */
  id: string
  label: string
  /** Material of every surface hit, in order. */
  materials: MaterialId[]
  /** Every bounce in order; occlusion is tested along source → contacts → listener. */
  contacts: Contact[]
  /** First contact point and normal (kept for single-bounce callers). */
  point: V3
  normal: V3
  length: number
  /** Where the sound seems to come from, seen from the listener. */
  apparent: V3
  /** Level change from wall curvature relative to a flat mirror (convex spreads, concave focuses). */
  spread: number
}

const TAU = Math.PI * 2
const SAMPLES = 96
/** Curved second-order searches use a coarser scan: they are weaker paths and there are many more of them. */
const SECOND_ORDER_SAMPLES = 48
const unit = (i: 0 | 1 | 2): V3 => (i === 0 ? [1, 0, 0] : i === 1 ? [0, 1, 0] : [0, 0, 1])

// ---- Building the reflector list -----------------------------------------------------------------

const ellipseRegion =
  (rx: number, rz: number, rInner = 0) =>
  (s: number, t: number) =>
    (s / rx) ** 2 + (t / rz) ** 2 <= 1 && s * s + t * t >= rInner * rInner

const rectRegion = (hu: number, hv: number) => (s: number, t: number) => Math.abs(s) <= hu && Math.abs(t) <= hv

export function roomReflectors(room: RoomInput): Reflector[] {
  const [w, h, d] = room.size
  const out: Reflector[] = []
  const plane = (
    id: string,
    label: string,
    o: V3,
    n: V3,
    u: V3,
    v: V3,
    region: PlaneReflector['region'],
    face: Parameters<typeof materialOf>[1],
  ) => out.push({ kind: 'plane', id: `room:${id}`, label, material: materialOf(room, face), o, n, u, v, region })

  if (shapeOf(room) === 'box') {
    const xz = rectRegion(w / 2, d / 2)
    plane('floor', 'Floor', [0, 0, 0], [0, 1, 0], [1, 0, 0], [0, 0, 1], xz, 'floor')
    plane('ceiling', 'Ceiling', [0, h, 0], [0, -1, 0], [1, 0, 0], [0, 0, 1], xz, 'ceiling')
    plane('west', 'West wall', [-w / 2, h / 2, 0], [1, 0, 0], [0, 0, 1], [0, 1, 0], rectRegion(d / 2, h / 2), 'west')
    plane('east', 'East wall', [w / 2, h / 2, 0], [-1, 0, 0], [0, 0, 1], [0, 1, 0], rectRegion(d / 2, h / 2), 'east')
    plane('north', 'North wall', [0, h / 2, -d / 2], [0, 0, 1], [1, 0, 0], [0, 1, 0], rectRegion(w / 2, h / 2), 'north')
    plane('south', 'South wall', [0, h / 2, d / 2], [0, 0, -1], [1, 0, 0], [0, 1, 0], rectRegion(w / 2, h / 2), 'south')
    return out
  }

  if (shapeOf(room) === 'polygon') {
    // Custom outline: a flat patch per wall, with the interior on the left of each counter-clockwise edge.
    const c = outlineOf(room)
    const inside = (s: number, t: number) => pointInContour(c, s, t)
    plane('floor', 'Floor', [0, 0, 0], [0, 1, 0], [1, 0, 0], [0, 0, 1], inside, 'floor')
    plane('ceiling', 'Ceiling', [0, h, 0], [0, -1, 0], [1, 0, 0], [0, 0, 1], inside, 'ceiling')
    c.forEach((a, i) => {
      const b = c[(i + 1) % c.length]!
      const dx = b[0] - a[0]
      const dz = b[1] - a[1]
      const l = Math.hypot(dx, dz)
      if (l < 1e-6) return
      plane(
        `wall${i}`,
        `Wall ${i + 1}`,
        [(a[0] + b[0]) / 2, h / 2, (a[1] + b[1]) / 2],
        [-dz / l, 0, dx / l],
        [dx / l, 0, dz / l],
        [0, 1, 0],
        rectRegion(l / 2, h / 2),
        'wall',
      )
    })
    return out
  }

  const disc = ellipseRegion(w / 2, d / 2, coreRadius(room))
  plane('floor', 'Floor', [0, 0, 0], [0, 1, 0], [1, 0, 0], [0, 0, 1], disc, 'floor')
  plane('ceiling', 'Ceiling', [0, h, 0], [0, -1, 0], [1, 0, 0], [0, 0, 1], disc, 'ceiling')
  const wall = makeFrame(wallShape(room))
  out.push({
    kind: 'cyl',
    id: 'room:wall',
    label: 'Outer wall',
    material: materialOf(room, 'wall'),
    frame: wall,
    rx: w / 2,
    rz: d / 2,
    y0: -h / 2,
    y1: h / 2,
    inward: true,
  })
  const core = coreShape(room)
  if (core) {
    const f = makeFrame(core)
    out.push({
      kind: 'cyl',
      id: 'room:core',
      label: 'Core column',
      material: core.material,
      frame: f,
      rx: f.half[0],
      rz: f.half[2],
      y0: -h / 2,
      y1: h / 2,
      inward: false,
    })
  }
  return out
}

/** Reflecting surfaces of one primitive, tagged with their owner. */
export function objectReflectors(f: Frame): Reflector[] {
  const owner: Owner = { id: f.shape.id, position: f.shape.position, radius: Math.hypot(...f.half) }
  return surfacesOf(f).map((r) => ({ ...r, owner }))
}

/** Faces of a solid face outwards; a tube also reflects inside its bore. */
function surfacesOf(f: Frame): Reflector[] {
  const s = f.shape
  const name = s.name ?? s.id
  const out: Reflector[] = []
  const axisDir = (i: 0 | 1 | 2): V3 => toWorldDir(f, unit(i))
  const pos = s.position

  if (s.kind === 'box') {
    for (const a of [0, 1, 2] as const) {
      const ua = ((a + 1) % 3) as 0 | 1 | 2
      const va = ((a + 2) % 3) as 0 | 1 | 2
      for (const sign of [1, -1]) {
        const n = scale(axisDir(a), sign)
        out.push({
          kind: 'plane',
          id: `${s.id}:${sign > 0 ? '+' : '-'}${'xyz'[a]}`,
          label: `${name} ${sign > 0 ? '+' : '−'}${'XYZ'[a]}`,
          material: s.material,
          o: add(pos, scale(n, f.half[a])),
          n,
          u: axisDir(ua),
          v: axisDir(va),
          region: rectRegion(f.half[ua], f.half[va]),
        })
      }
    }
    return out
  }

  if (s.kind === 'prism' && f.contours) {
    const contours = f.contours
    const hhP = f.half[1]
    const up = axisDir(1)
    contours.forEach((c, ci) => {
      c.forEach((a, i) => {
        const b = c[(i + 1) % c.length]!
        const dx = b[0] - a[0]
        const dz = b[1] - a[1]
        const l = Math.hypot(dx, dz)
        if (l < 1e-6) return
        const n2 = outwardNormal(contours, ci, i)
        out.push({
          kind: 'plane',
          id: `${s.id}:e${ci}.${i}`,
          label: `${name} ${ci === 0 ? 'wall' : `hole ${ci} wall`} ${i + 1}`,
          material: s.material,
          o: toWorld(f, [(a[0] + b[0]) / 2, 0, (a[1] + b[1]) / 2]),
          n: normalize(toWorldDir(f, [n2[0], 0, n2[1]])),
          u: toWorldDir(f, [dx / l, 0, dz / l]),
          v: up,
          region: rectRegion(l / 2, hhP),
        })
      })
    })
    const inRegion = (s2: number, t: number) => pointInRegion(contours, s2, t)
    for (const sign of [1, -1]) {
      out.push({
        kind: 'plane',
        id: `${s.id}:${sign > 0 ? 'top' : 'bottom'}`,
        label: `${name} ${sign > 0 ? 'top' : 'bottom'}`,
        material: s.material,
        o: add(pos, scale(up, sign * hhP)),
        n: scale(up, sign),
        u: axisDir(0),
        v: axisDir(2),
        region: inRegion,
      })
    }
    return out
  }

  const [hx, hh, hz] = f.half
  out.push({
    kind: 'cyl',
    id: `${s.id}:side`,
    label: `${name} side`,
    material: s.material,
    frame: f,
    rx: hx,
    rz: hz,
    y0: -hh,
    y1: hh,
    inward: false,
  })
  const up = axisDir(1)
  const endPlate = (y: number, radii: [number, number], faceSign: 1 | -1, label: string, id: string) => {
    const n = scale(up, faceSign)
    out.push({
      kind: 'plane',
      id: `${s.id}:${id}`,
      label: `${name} ${label}`,
      material: s.material,
      o: add(pos, scale(up, y)),
      n,
      u: axisDir(0),
      v: axisDir(2),
      region: ellipseRegion(radii[0], radii[1]),
    })
  }
  if (s.kind === 'cylinder') {
    endPlate(hh, [hx, hz], 1, 'top', 'top')
    endPlate(-hh, [hx, hz], -1, 'bottom', 'bottom')
    return out
  }

  // Tube: outer surface above, plus the bore and any end plates.
  const b = f.bore
  if (!b) return out
  out.push({
    kind: 'cyl',
    id: `${s.id}:bore`,
    label: `${name} bore`,
    material: s.material,
    frame: f,
    rx: b.rx,
    rz: b.rz,
    y0: Math.max(b.y0, -hh),
    y1: Math.min(b.y1, hh),
    inward: true,
  })
  const wall = Math.min(Math.max(s.wall ?? 1, 0.01), 0.98 * Math.min(hx, hz))
  const caps = s.caps ?? 'none'
  if (caps === 'top' || caps === 'both') {
    endPlate(hh, [hx, hz], 1, 'top cap', 'cap-top-out')
    endPlate(hh - wall, [b.rx, b.rz], -1, 'top cap (inside)', 'cap-top-in')
  }
  if (caps === 'bottom' || caps === 'both') {
    endPlate(-hh, [hx, hz], -1, 'bottom cap', 'cap-bottom-out')
    endPlate(-hh + wall, [b.rx, b.rz], 1, 'bottom cap (inside)', 'cap-bottom-in')
  }
  return out
}

// ---- Solving -------------------------------------------------------------------------------------

function planeCandidate(r: PlaneReflector, S: V3, L: V3): Candidate | null {
  const dS = dot(sub(S, r.o), r.n)
  const dL = dot(sub(L, r.o), r.n)
  if (dS <= 1e-4 || dL <= 1e-4) return null
  const image = sub(S, scale(r.n, 2 * dS))
  // The mirrored source sits dS behind the plane and the listener dL in front: the line between them crosses at t = dS / (dS + dL).
  const P = add(image, scale(sub(L, image), dS / (dS + dL)))
  const rel = sub(P, r.o)
  if (!r.region(dot(rel, r.u), dot(rel, r.v))) return null
  return {
    order: 1,
    reflector: r,
    id: r.id,
    label: r.label,
    materials: [r.material],
    contacts: [{ point: P, normal: r.n }],
    point: P,
    normal: r.n,
    length: dist(image, L),
    apparent: image,
    spread: 1,
  }
}

function cylCandidates(r: CylReflector, S: V3, L: V3, samples = SAMPLES): Candidate[] {
  const f = r.frame
  const s = toLocal(f, S)
  const l = toLocal(f, L)
  const { rx, rz } = r
  // A point inside a column's footprint (or outside a hall's wall) can never reflect off that surface: skip the scan.
  const ks = Math.hypot(s[0] / rx, s[2] / rz)
  const kl = Math.hypot(l[0] / rx, l[2] / rz)
  if (r.inward ? ks >= 1 || kl >= 1 : ks <= 1 || kl <= 1) return []

  /** Derivative of (|S−P| + |L−P|) along the ellipse: zero where the reflection law holds. */
  const g = (th: number): number => {
    const px = rx * Math.cos(th)
    const pz = rz * Math.sin(th)
    const sx = s[0] - px
    const sz = s[2] - pz
    const lx = l[0] - px
    const lz = l[2] - pz
    const ds = Math.hypot(sx, sz)
    const dl = Math.hypot(lx, lz)
    if (ds < 1e-9 || dl < 1e-9) return Number.NaN
    const tx = -rx * Math.sin(th)
    const tz = rz * Math.cos(th)
    const tn = Math.hypot(tx, tz)
    return (tx * (sx / ds + lx / dl) + tz * (sz / ds + lz / dl)) / tn
  }

  const roots: number[] = []
  let prevTh = 0
  let prev = g(0)
  for (let i = 1; i <= samples; i++) {
    const th = (i / samples) * TAU
    const cur = g(th)
    if (Number.isFinite(prev) && Number.isFinite(cur) && prev * cur <= 0 && !(prev === 0 && cur === 0)) {
      let a = prevTh
      let b = th
      let ga = prev
      for (let k = 0; k < 40; k++) {
        const m = (a + b) / 2
        const gm = g(m)
        if (!Number.isFinite(gm)) break
        if (ga * gm <= 0) b = m
        else {
          a = m
          ga = gm
        }
      }
      const root = ((a + b) / 2) % TAU
      if (!roots.some((x) => Math.abs(x - root) < 1e-4 || Math.abs(Math.abs(x - root) - TAU) < 1e-4)) roots.push(root)
    }
    prevTh = th
    prev = cur
  }

  const out: Candidate[] = []
  for (const th of roots) {
    const c = Math.cos(th)
    const sn = Math.sin(th)
    const px = rx * c
    const pz = rz * sn
    const nOut = normalize([c / rx, 0, sn / rz])
    const face: V3 = r.inward ? [-nOut[0], 0, -nOut[2]] : nOut
    const sv: V3 = [s[0] - px, 0, s[2] - pz]
    const lv: V3 = [l[0] - px, 0, l[2] - pz]
    const d1 = Math.hypot(sv[0], sv[2])
    const d2 = Math.hypot(lv[0], lv[2])
    const sh = normalize(sv)
    const lh = normalize(lv)
    const cosPhi = dot(sh, face)
    if (cosPhi < 1e-4 || dot(lh, face) < 1e-4) continue // source or listener is behind this part of the wall
    const yP = s[1] + ((l[1] - s[1]) * d1) / (d1 + d2)
    if (yP < r.y0 || yP > r.y1) continue

    const length = Math.hypot(d1 + d2, l[1] - s[1])
    const point = toWorld(f, [px, yP, pz])
    const normal = normalize(toWorldDir(f, face))
    const toPoint = normalize(sub(point, L))
    // Curvature: the reflected wave's virtual source distance ρ′ follows 1/ρ′ = 1/d1 ± 2κ/cosφ (+ convex, − concave).
    const kappa = (rx * rz) / (rx * rx * sn * sn + rz * rz * c * c) ** 1.5
    const inv = 1 / d1 + ((r.inward ? -2 : 2) * kappa) / cosPhi
    const rho = Math.abs(inv) < 1e-12 ? 1e12 : 1 / inv
    const ratio = Math.abs(rho) / Math.max(Math.abs(rho + d2), 0.05 * (d1 + d2))
    const spread = Math.min(2, Math.max(0.1, Math.sqrt(ratio * ((d1 + d2) / d1))))
    out.push({
      order: 1,
      reflector: r,
      id: `${r.id}@${Math.round((th / TAU) * 64) % 64}`,
      label: r.label,
      materials: [r.material],
      contacts: [{ point, normal }],
      point,
      normal,
      length,
      apparent: add(L, scale(toPoint, length)),
      spread,
    })
  }
  return out
}

/** Every geometrically valid first-order reflection from the given surfaces (occlusion is checked later). */
export function reflectionCandidates(reflectors: Reflector[], source: V3, listener: V3): Candidate[] {
  const out: Candidate[] = []
  for (const r of reflectors) {
    if (r.kind === 'plane') {
      const c = planeCandidate(r, source, listener)
      if (c) out.push(c)
    } else out.push(...cylCandidates(r, source, listener))
  }
  return out
}

// ---- Second order --------------------------------------------------------------------------------

const frontOf = (r: PlaneReflector, p: V3) => dot(sub(p, r.o), r.n)
const mirrorAcross = (p: V3, r: PlaneReflector): V3 => sub(p, scale(r.n, 2 * frontOf(r, p)))
const onPatch = (r: PlaneReflector, p: V3) => {
  const rel = sub(p, r.o)
  return r.region(dot(rel, r.u), dot(rel, r.v))
}

function makeSecond(a: Reflector, b: Reflector, c1: Contact, c2: Contact, length: number, L: V3, spread: number, id?: string): Candidate {
  return {
    order: 2,
    reflector: a,
    id: id ?? `${a.id}>${b.id}`,
    label: `${a.label} → ${b.label}`,
    materials: [a.material, b.material],
    contacts: [c1, c2],
    point: c1.point,
    normal: c1.normal,
    length,
    // The sound arrives along the last leg: seen from the listener it comes from the second contact.
    apparent: add(L, scale(normalize(sub(c2.point, L)), length)),
    spread,
  }
}

/** Flat then flat: two mirror images. */
function planePlane(A: PlaneReflector, B: PlaneReflector, S: V3, L: V3): Candidate | null {
  const dSA = frontOf(A, S)
  const dLB = frontOf(B, L)
  if (dSA <= 1e-4 || dLB <= 1e-4) return null
  const S1 = mirrorAcross(S, A)
  const dS1B = frontOf(B, S1)
  if (dS1B <= 1e-4) return null
  const S2 = mirrorAcross(S1, B)
  const P2 = add(S2, scale(sub(L, S2), dS1B / (dS1B + dLB)))
  if (!onPatch(B, P2)) return null
  const dP2A = frontOf(A, P2)
  if (dP2A <= 1e-4) return null
  const P1 = add(S1, scale(sub(P2, S1), dSA / (dSA + dP2A)))
  if (!onPatch(A, P1)) return null
  return makeSecond(A, B, { point: P1, normal: A.n }, { point: P2, normal: B.n }, dist(S2, L), L, 1)
}

/** Flat then curved: mirror the source in the flat surface and solve the curved reflection from the image. */
function planeThenCyl(A: PlaneReflector, B: CylReflector, S: V3, L: V3): Candidate[] {
  const dSA = frontOf(A, S)
  if (dSA <= 1e-4) return []
  const S1 = mirrorAcross(S, A)
  const out: Candidate[] = []
  for (const c of cylCandidates(B, S1, L, SECOND_ORDER_SAMPLES)) {
    const P2 = c.contacts[0]!.point
    const dP2A = frontOf(A, P2)
    if (dP2A <= 1e-4) continue
    const P1 = add(S1, scale(sub(P2, S1), dSA / (dSA + dP2A)))
    if (!onPatch(A, P1)) continue
    out.push(
      makeSecond(A, B, { point: P1, normal: A.n }, { point: P2, normal: c.contacts[0]!.normal }, c.length, L, c.spread, `${A.id}>${c.id}`),
    )
  }
  return out
}

/** Curved then flat: mirror the listener in the flat surface and solve the curved reflection towards the image. */
function cylThenPlane(A: CylReflector, B: PlaneReflector, S: V3, L: V3): Candidate[] {
  const dLB = frontOf(B, L)
  if (dLB <= 1e-4) return []
  const L1 = mirrorAcross(L, B)
  const out: Candidate[] = []
  for (const c of cylCandidates(A, S, L1, SECOND_ORDER_SAMPLES)) {
    const P1 = c.contacts[0]!.point
    const dP1B = frontOf(B, P1)
    if (dP1B <= 1e-4) continue
    const P2 = add(P1, scale(sub(L1, P1), dP1B / (dP1B + dLB)))
    if (!onPatch(B, P2)) continue
    out.push(
      makeSecond(A, B, { point: P1, normal: c.contacts[0]!.normal }, { point: P2, normal: B.n }, c.length, L, c.spread, `${c.id}>${B.id}`),
    )
  }
  return out
}

/**
 * Two-bounce reflections (corners of a room, a pillar and the floor, a block and a wall…). To stay cheap the search is
 * limited to the room's surfaces plus the primitives nearest the source–listener line, and only the `keep` strongest
 * candidates are returned. A curve followed by another curve is not solved.
 */
export function secondOrderCandidates(reflectors: Reflector[], S: V3, L: V3, nearest = 6, keep = 24): Candidate[] {
  const mid = scale(add(S, L), 0.5)
  const owners = new Map<string, number>()
  for (const r of reflectors) {
    if (r.owner && !owners.has(r.owner.id)) owners.set(r.owner.id, dist(r.owner.position, mid) - r.owner.radius)
  }
  const near = new Set(
    [...owners.entries()]
      .sort((a, b) => a[1] - b[1])
      .slice(0, nearest)
      .map(([id]) => id),
  )
  const pool = reflectors.filter((r) => !r.owner || near.has(r.owner.id))
  const planes = pool.filter((r): r is PlaneReflector & { owner?: Owner } => r.kind === 'plane')
  const cyls = pool.filter((r): r is CylReflector & { owner?: Owner } => r.kind === 'cyl')
  const seeing = planes.filter((p) => frontOf(p, S) > 1e-4)
  const heard = planes.filter((p) => frontOf(p, L) > 1e-4)

  const out: Candidate[] = []
  for (const a of seeing) {
    for (const b of heard) {
      if (a === b) continue
      const c = planePlane(a, b, S, L)
      if (c) out.push(c)
    }
    for (const b of cyls) out.push(...planeThenCyl(a, b, S, L))
  }
  for (const a of cyls) for (const b of heard) out.push(...cylThenPlane(a, b, S, L))

  const strength = (c: Candidate) => c.materials.reduce((g, m) => g * Math.sqrt(1 - MATERIALS[m].absLow), c.spread) / Math.max(c.length, 1)
  return out.sort((x, y) => strength(y) - strength(x)).slice(0, keep)
}

/** All reflecting surfaces of a scene: the room plus every primitive. */
export function sceneReflectors(room: RoomInput, objectFrames: Frame[]): Reflector[] {
  return [...roomReflectors(room), ...objectFrames.flatMap(objectReflectors)]
}
